import { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { Bdg, Av, AiScoreRing, PH } from '../../components/shared';
import { useApp } from '../../context/AppContext';
import { getLeads as fetchLeads, addLead as apiAddLead, deleteLead as apiDeleteLead, updateLeadNote as apiUpdateLeadNote, updateLead as apiUpdateLead, getEnterpriseProfiles, sendLeadEmail as apiSendLeadEmail, getLeadActivity as apiGetLeadActivity } from '../../services/api';

const API_ORIGIN = (process.env.REACT_APP_API_BASE || 'http://teams.gotap.oo/api').replace(/\/api\/?$/, '');
const fullPhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url;
  return `${API_ORIGIN}/${url.replace(/^\//, '')}`;
};

// Searchable single-select used by the lead form. Closes on outside click.
const SearchableSelect = ({ value, onChange, options, placeholder = "Select…", error }) => {
  const [open, setOpen]   = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const selected = options.find(o => String(o.value) === String(value));
  const filtered = !query
    ? options
    : options.filter(o => (o.label || "").toLowerCase().includes(query.toLowerCase()));

  return (
    <div ref={ref} style={{position:"relative"}}>
      <button type="button" className="inp" onClick={() => setOpen(o=>!o)} style={{
        textAlign:"left", cursor:"pointer",
        borderColor: error ? "#ef4444" : "",
        display:"flex", alignItems:"center", justifyContent:"space-between", width:"100%",
      }}>
        <span style={{color: selected ? "#0f172a" : "#94a3b8", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
          {selected?.label || placeholder}
        </span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" style={{flexShrink:0, marginLeft:8}}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      {open && (
        <div style={{
          position:"absolute", top:"calc(100% + 4px)", left:0, right:0, zIndex:60,
          background:"#fff", border:"1px solid rgba(0,0,0,.1)", borderRadius:11,
          boxShadow:"0 16px 44px rgba(0,0,0,.14)", overflow:"hidden",
        }}>
          <div style={{padding:6, borderBottom:"1px solid rgba(0,0,0,.06)"}}>
            <input
              autoFocus className="inp"
              placeholder="Search…"
              value={query}
              onChange={e=>setQuery(e.target.value)}
              style={{fontSize:12.5, padding:"6px 10px"}}
            />
          </div>
          <div style={{maxHeight:200, overflowY:"auto"}}>
            {filtered.length === 0 ? (
              <div style={{padding:"12px 14px", fontSize:12, color:"#94a3b8", textAlign:"center"}}>No matches</div>
            ) : filtered.map(o => {
              const isActive = String(o.value) === String(value);
              return (
                <div key={o.value} onClick={()=>{ onChange(o.value); setOpen(false); setQuery(""); }}
                  style={{
                    padding:"8px 14px", fontSize:12.5, cursor:"pointer",
                    background: isActive ? "rgba(8,145,178,.08)" : "transparent",
                    color:      isActive ? "#0891b2" : "#0f172a",
                    fontWeight: isActive ? 660 : 500,
                  }}
                  onMouseEnter={e=>{ if(!isActive) e.currentTarget.style.background = "rgba(0,0,0,.04)"; }}
                  onMouseLeave={e=>{ if(!isActive) e.currentTarget.style.background = "transparent"; }}>
                  {o.label}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Single note in the Notes tab. Lifted out so its `editing` state isn't recreated
// on every parent render (the panel is a function component nested in PageLeads,
// so re-renders happen often).
const NoteRow = ({ note, accentColor, editing, onStartEdit, onCancelEdit, onSave, onDelete }) => {
  const [draft, setDraft] = useState(note);
  useEffect(()=>{ setDraft(note); }, [note, editing]);

  if (editing) {
    return (
      <div style={{
        padding:"11px 13px", borderRadius:11,
        background:"rgba(255,255,255,.92)",
        border:`1px solid ${accentColor}33`,
        boxShadow:`0 4px 14px ${accentColor}1a`,
      }}>
        <textarea
          value={draft}
          onChange={e=>setDraft(e.target.value)}
          autoFocus
          style={{
            width:"100%", minHeight:64, resize:"vertical",
            border:"1px solid rgba(0,0,0,.1)", borderRadius:8,
            padding:"8px 10px", fontFamily:"var(--f)", fontSize:12.5,
            color:"#0f172a", background:"#fff",
            outline:"none", boxSizing:"border-box", lineHeight:1.55,
          }}
        />
        <div style={{display:"flex", gap:6, justifyContent:"flex-end", marginTop:7}}>
          <button onClick={onCancelEdit} style={{
            padding:"5px 12px", borderRadius:7, fontSize:11.5, fontWeight:600,
            border:"1px solid rgba(0,0,0,.1)", background:"rgba(0,0,0,.03)",
            color:"#64748b", cursor:"pointer",
          }}>Cancel</button>
          <button onClick={()=>onSave(draft)} disabled={!draft.trim()} style={{
            padding:"5px 14px", borderRadius:7, fontSize:11.5, fontWeight:660,
            border:"none", background:draft.trim()?accentColor:"rgba(0,0,0,.15)",
            color:"#fff", cursor:draft.trim()?"pointer":"not-allowed",
          }}>Save</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display:"flex", gap:10, padding:"11px 13px",
      borderRadius:11, background:"rgba(255,255,255,.8)",
      border:"1px solid rgba(0,0,0,.07)",
    }}>
      <div style={{width:5,height:5,borderRadius:"50%",background:accentColor,marginTop:7,flexShrink:0}}/>
      <div style={{flex:1,fontSize:12.5,color:"#334155",lineHeight:1.6,whiteSpace:"pre-wrap"}}>{note}</div>
      <div style={{display:"flex", gap:2, alignSelf:"flex-start"}}>
        <button title="Edit" onClick={onStartEdit} style={{
          width:22,height:22,border:"none",background:"transparent",
          cursor:"pointer",fontSize:11,color:"#94a3b8",flexShrink:0,
          display:"flex",alignItems:"center",justifyContent:"center",
          borderRadius:5,transition:"all .12s",
        }}
          onMouseEnter={e=>{e.currentTarget.style.background="rgba(8,145,178,.1)";e.currentTarget.style.color="#0891b2";}}
          onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color="#94a3b8";}}>✎</button>
        <button title="Delete" onClick={onDelete} style={{
          width:22,height:22,border:"none",background:"transparent",
          cursor:"pointer",fontSize:10,color:"#cbd5e1",flexShrink:0,
          display:"flex",alignItems:"center",justifyContent:"center",
          borderRadius:5,transition:"all .12s",
        }}
          onMouseEnter={e=>{e.currentTarget.style.background="rgba(220,38,38,.08)";e.currentTarget.style.color="#dc2626";}}
          onMouseLeave={e=>{e.currentTarget.style.background="transparent";e.currentTarget.style.color="#cbd5e1";}}>✕</button>
      </div>
    </div>
  );
};

// Stacked email-compose modal. Higher z-index so it sits above LeadDetailPanel.
const LeadEmailModal = ({ lead, onClose, onSend, sender }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody]       = useState("");
  const [sending, setSending] = useState(false);

  useEffect(()=>{
    const onKey = e => { if(e.key==="Escape" && !sending) onClose(); };
    document.addEventListener("keydown", onKey);
    return ()=>document.removeEventListener("keydown", onKey);
  }, [onClose, sending]);

  const canSend = subject.trim() && body.trim() && !sending;

  const submit = async () => {
    if(!canSend) return;
    setSending(true);
    try { await onSend(subject.trim(), body.trim()); }
    finally { setSending(false); }
  };

  return createPortal(
    <div onClick={e=>{ if(e.target===e.currentTarget && !sending) onClose(); }} style={{
      position:"fixed", inset:0, zIndex:600,
      display:"flex", alignItems:"center", justifyContent:"center",
      padding:"28px 24px",
      background:"rgba(10,14,26,.55)",
      backdropFilter:"blur(14px)", WebkitBackdropFilter:"blur(14px)",
    }}>
      <div style={{
        width:"100%", maxWidth:560,
        maxHeight:"calc(100vh - 56px)",
        background:"#fff", borderRadius:20,
        boxShadow:"0 48px 120px rgba(0,0,0,.32), 0 0 0 1px rgba(255,255,255,.6)",
        display:"flex", flexDirection:"column", overflow:"hidden",
        animation:"cardReveal .22s cubic-bezier(.16,1,.3,1)",
      }}>

        {/* header */}
        <div style={{padding:"18px 22px 14px", borderBottom:"1px solid rgba(0,0,0,.06)", display:"flex", alignItems:"center", gap:12}}>
          <div style={{
            width:42, height:42, borderRadius:12,
            background:"rgba(8,145,178,.1)", border:"1px solid rgba(8,145,178,.18)",
            display:"flex", alignItems:"center", justifyContent:"center", fontSize:19,
          }}>📧</div>
          <div style={{flex:1, minWidth:0}}>
            <div style={{fontSize:15, fontWeight:720, color:"#0f172a", letterSpacing:"-.2px"}}>Email {lead.name}</div>
            <div style={{fontSize:11.5, color:"#94a3b8", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{lead.email}</div>
          </div>
          <button onClick={onClose} disabled={sending} style={{
            width:30, height:30, borderRadius:8,
            border:"1px solid rgba(0,0,0,.09)", background:"rgba(0,0,0,.04)",
            cursor:sending?"not-allowed":"pointer", fontSize:13, color:"#64748b",
            display:"flex", alignItems:"center", justifyContent:"center",
            opacity:sending?.5:1,
          }}>✕</button>
        </div>

        {/* sender banner */}
        <div style={{
          margin:"14px 22px 0", padding:"10px 12px",
          borderRadius:10,
          background:"linear-gradient(135deg,rgba(8,145,178,.07),rgba(124,58,237,.05))",
          border:"1px solid rgba(8,145,178,.16)",
          fontSize:11.5, color:"#475569", lineHeight:1.55,
        }}>
          Sending as <b style={{color:"#0f172a"}}>{sender.companyName || sender.name || "Your Company"}</b>
          {" · Replies go to "}
          <b style={{color:"#0f172a"}}>{sender.email || "your address on file"}</b>
        </div>

        {/* body */}
        <div style={{padding:"14px 22px 4px", display:"flex", flexDirection:"column", gap:10, overflowY:"auto"}}>
          <div className="fg" style={{margin:0}}>
            <label style={{fontSize:11, color:"#94a3b8", fontWeight:660, letterSpacing:".05em", textTransform:"uppercase"}}>To</label>
            <input className="inp" value={lead.email} readOnly disabled style={{cursor:"not-allowed", opacity:.85}}/>
          </div>
          <div className="fg" style={{margin:0}}>
            <label style={{fontSize:11, color:"#94a3b8", fontWeight:660, letterSpacing:".05em", textTransform:"uppercase"}}>Subject</label>
            <input className="inp" placeholder="Subject line" value={subject} onChange={e=>setSubject(e.target.value)} maxLength={255}/>
          </div>
          <div className="fg" style={{margin:0}}>
            <label style={{fontSize:11, color:"#94a3b8", fontWeight:660, letterSpacing:".05em", textTransform:"uppercase"}}>Message</label>
            <textarea className="inp" placeholder="Write your message…" value={body} onChange={e=>setBody(e.target.value)} maxLength={5000}
              style={{minHeight:160, resize:"vertical", fontSize:13, lineHeight:1.55}}/>
            <div style={{fontSize:10.5, color:"#cbd5e1", textAlign:"right", marginTop:4}}>{body.length}/5000</div>
          </div>
        </div>

        {/* footer */}
        <div style={{padding:"12px 22px 16px", borderTop:"1px solid rgba(0,0,0,.06)", display:"flex", justifyContent:"flex-end", gap:8}}>
          <button className="btn bs" onClick={onClose} disabled={sending} style={{padding:"8px 18px", fontSize:12.5}}>Cancel</button>
          <button className="btn bp" onClick={submit} disabled={!canSend} style={{
            padding:"8px 22px", fontSize:12.5, gap:6,
            opacity:canSend?1:.55, cursor:canSend?"pointer":"not-allowed",
          }}>{sending ? "Sending…" : "📤 Send Email"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const PageLeads = memo(()=>{
  const { showToast, user } = useApp();

  /* ══ LEADS DATA — loaded from API ══ */
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const SOURCE_MAP = {1:"NFC Tap",2:"Lead Form",3:"Manual",4:"Direct"};

  const mapLead = useCallback((l) => ({
    id: l.id,
    name: l.name || "",
    email: l.email || "",
    phone: l.phone || "",
    company: l.company || "",
    industry: l.industry || "",
    source: SOURCE_MAP[l.type] || "Manual",
    status: l.status || "new",
    date: l.created_date ? new Date(l.created_date).toLocaleDateString("en",{month:"short",day:"numeric"}) : "",
    aiScore: l.score || 0,
    assignee: l.viewing_username || null,
    country: l.country || "",
    state: l.state || "",
    city: l.city || "",
    address: l.address || "",
    job_title: l.job_title || "",
    website: l.website || "",
    linkedin_url: l.linkedin_url || "",
    photo: fullPhotoUrl(l.photo),
    notes: l.note ? l.note.split("\n---\n").map(s => s.trim()).filter(Boolean) : [],
    type: l.type,
    profile_id: l.profile_id || l.viewer_id || null,
    created_at: l.created_date || l.created_at || null,
    updated_at: l.updated_at || null,
  }), []);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchLeads();
      const items = res.data || res;
      if (Array.isArray(items)) {
        setLeads(items.map(mapLead));
      }
    } catch (e) {
      console.error("Failed to load leads:", e);
    } finally {
      setLoading(false);
    }
  }, [mapLead]);

  useEffect(() => { loadLeads(); }, [loadLeads]);

  const [filter,    setFilter]    = useState("all");
  const [search,    setSearch]    = useState("");
  const [preview,   setPreview]   = useState(null);   // lead id for detail panel
  const [showAdd,   setShowAdd]   = useState(false);  // add lead modal
  const [editLead,  setEditLead]  = useState(null);   // lead object for edit modal
  const [delConfirm,setDelConfirm]= useState(null);   // lead id to confirm delete
  const [noteInput, setNoteInput] = useState("");     // note text per preview
  const [addNoteFor,setAddNoteFor]= useState(null);   // lead id quick-note
  const [statusDrop,setStatusDrop]= useState(null);   // lead id for status dropdown
  const [emailLead, setEmailLead] = useState(null);   // lead object for email modal
  const [editingNote,setEditingNote] = useState(null); // {leadId, idx} for inline note edit
  const [previewIntent,setPreviewIntent] = useState({tab:"contact", nonce:0}); // controls which tab opens

  // Open the detail modal for `id` and request a specific tab. Nonce bumps so the
  // panel's effect fires even when the same tab is requested twice in a row.
  const openPreviewWith = useCallback((id, tab="contact") => {
    setPreviewIntent(p => ({tab, nonce: p.nonce + 1}));
    setPreview(id);
  }, []);

  // Close status dropdown on outside click
  useEffect(()=>{
    if(!statusDrop) return;
    const h = ()=>setStatusDrop(null);
    document.addEventListener("click",h);
    return ()=>document.removeEventListener("click",h);
  },[statusDrop]);

  const rows = leads.filter(l =>
    (filter==="all"||l.status===filter) &&
    (search===""||l.name.toLowerCase().includes(search.toLowerCase())||
     l.company.toLowerCase().includes(search.toLowerCase())||
     l.email.toLowerCase().includes(search.toLowerCase()))
  );

  const selectedLead = leads.find(l=>l.id===preview);

  const updateLead = (id, patch) => setLeads(ls=>ls.map(l=>l.id===id?{...l,...patch}:l));
  const handleDeleteLead = async (id) => {
    try {
      await apiDeleteLead(id);
      setLeads(ls=>ls.filter(l=>l.id!==id));
      setPreview(null); setDelConfirm(null);
      showToast("Lead deleted");
    } catch {
      showToast("Failed to delete lead");
    }
  };
  const addNote = (id, text) => {
    if (!text.trim()) return;
    const lead = leads.find(l => l.id === id);
    if (!lead) return;
    const allNotes = [...(lead.notes || []), text.trim()];
    // Optimistic: render the new note immediately and clear the input.
    // The server write happens in the background; failure surfaces as a toast.
    updateLead(id, { notes: allNotes });
    setNoteInput(""); setAddNoteFor(null);
    apiUpdateLeadNote(id, allNotes.join("\n---\n"))
      .then(() => showToast("Note added"))
      .catch(() => showToast("Saved locally — sync failed"));
  };

  // Replaces note at idx with text (or removes it if text is empty/null).
  // Backend stores one combined string, so we always re-save the joined notes.
  const saveNoteAt = async (id, idx, text) => {
    const lead = leads.find(l=>l.id===id);
    if(!lead) return;
    const trimmed = (text||"").trim();
    const next = [...(lead.notes||[])];
    if(!trimmed) next.splice(idx,1);
    else next[idx] = trimmed;
    updateLead(id,{notes: next});
    try { await apiUpdateLeadNote(id, next.join("\n---\n")); showToast(trimmed?"Note updated":"Note removed"); }
    catch { showToast("Failed to save note"); }
  };

  const changeStatus = async (id, newStatus) => {
    const lead = leads.find(l=>l.id===id);
    if(!lead || lead.status===newStatus){ setStatusDrop(null); return; }
    updateLead(id, {status:newStatus});
    setStatusDrop(null);
    try { await apiUpdateLead(id, {status:newStatus}); showToast(`Status changed to ${newStatus}`); }
    catch { updateLead(id, {status:lead.status}); showToast("Failed to update status"); }
  };

  const STATUSES = ["new","contacted","qualified","closed"];

  /* ── CSV download ── */
  const downloadCSV = (lead) => {
    const headers = ["Name","Email","Phone","Company","Industry","Country","State","City","Status","AI Score","Source","Date","Notes"];
    const row     = [lead.name,lead.email,lead.phone,lead.company,lead.industry,lead.country,lead.state,lead.city,lead.status,lead.aiScore,lead.source,lead.date,(lead.notes||[]).join(" | ")];
    const csv     = [headers,row].map(r=>r.map(v=>`"${v||""}"`).join(",")).join("\n");
    const blob    = new Blob([csv],{type:"text/csv"});
    const url     = URL.createObjectURL(blob);
    const a       = document.createElement("a");
    a.href=url; a.download=`lead_${lead.name.replace(/\s+/g,"_")}.csv`; a.click();
    URL.revokeObjectURL(url);
    showToast(`${lead.name} exported as CSV`);
  };

  const statusCol = {new:"#0891b2",contacted:"#d97706",qualified:"#059669",closed:"#64748b"};
  const statusBg  = {new:"rgba(8,145,178,.1)",contacted:"rgba(217,119,6,.1)",qualified:"rgba(5,150,105,.1)",closed:"rgba(100,116,139,.1)"};

  // Stable refs for handlers + state values used inside the memoized modal components.
  // Reassigned on every render so the modals always read the latest values without
  // changing component identity (which would otherwise unmount/remount on each render).
  const handlersRef = useRef({});
  handlersRef.current = {
    setPreview, setEditLead, setDelConfirm, setEmailLead,
    editingNote, setEditingNote,
    noteInput, setNoteInput, addNoteFor, setAddNoteFor,
    showToast, downloadCSV, changeStatus, addNote, saveNoteAt,
    handleDeleteLead,
  };

  /* ══════════════ LEAD DETAIL PANEL ══════════════ */
  const LeadDetailPanel = useMemo(() => function LeadDetailPanel({lead, initialTab, tabNonce}) {
    const {
      setPreview, setEditLead, setDelConfirm, setEmailLead,
      editingNote, setEditingNote,
      noteInput, setNoteInput, addNoteFor, setAddNoteFor,
      showToast, downloadCSV, changeStatus, addNote, saveNoteAt,
    } = handlersRef.current;

    const [tab,       setTab]       = useState(initialTab || "contact");
    const [localNote, setLocalNote] = useState("");

    // Honor parent-requested tab changes (e.g. clicking comment icon → Notes tab).
    // Nonce bumps on each click so this fires even for repeat requests.
    useEffect(() => {
      if (initialTab) setTab(initialTab);

    }, [tabNonce]);

    useEffect(() => {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }, []);

    const STATUS_LABEL = {new:"New Lead", contacted:"Contacted", qualified:"Qualified", closed:"Closed Won"};
    const col          = statusCol[lead.status] || "#64748b";
    const initials     = lead.name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase();
    const nfcTaps      = ((lead.id * 7) % 12) + 1;
    const daysActive   = ((lead.id * 13) % 30) + 5;
    const scoreColor   = lead.aiScore >= 85 ? "#059669" : lead.aiScore >= 65 ? "#d97706" : "#dc2626";
    const scoreLabel   = lead.aiScore >= 85 ? "High Priority" : lead.aiScore >= 65 ? "Mid Funnel" : "Early Stage";
    const aiInsight    = lead.aiScore >= 90
      ? `Score ${lead.aiScore} — strong purchase intent. Recommend immediate personal outreach within 24h.`
      : lead.aiScore >= 70
      ? `Score ${lead.aiScore} — showing interest signals. Follow up within 48h for best results.`
      : `Score ${lead.aiScore} — early stage. Nurture with content before direct sales approach.`;

    // Format absolute timestamps as Today/Yesterday/short date for compact display.
    const fmtTime = (raw) => {
      if (!raw) return "—";
      const d = new Date(raw);
      if (isNaN(d.getTime())) return String(raw);
      const now = new Date();
      const sameDay = d.toDateString() === now.toDateString();
      const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
      const isYesterday = d.toDateString() === yesterday.toDateString();
      const time = d.toLocaleTimeString("en", {hour:"numeric", minute:"2-digit"}).toLowerCase().replace(" ","");
      if (sameDay) return `Today ${time}`;
      if (isYesterday) return `Yesterday ${time}`;
      const sameYear = d.getFullYear() === now.getFullYear();
      return d.toLocaleDateString("en", sameYear
        ? {month:"short", day:"numeric"}
        : {month:"short", day:"numeric", year:"numeric"});
    };

    // Visual mapping from event type → icon + accent color. Adding a new
    // event type later only needs an entry here, no other UI change required.
    const ACTIVITY_VISUAL = {
      created:        {ico:"➕",  c:"#0891b2"},
      updated:        {ico:"✏",  c:"#0891b2"},
      status_changed: {ico:"🎯",  c:"#d97706"},
      reassigned:     {ico:"👤",  c:"#64748b"},
      note_added:     {ico:"📝",  c:"#059669"},
      note_updated:   {ico:"📝",  c:"#059669"},
      note_deleted:   {ico:"🗑",  c:"#dc2626"},
      email_sent:     {ico:"📧",  c:"#7c3aed"},
    };

    // Fetch the real activity log when the Activity tab is opened.
    // Re-fetched whenever lead.id changes or the user revisits the tab.
    const [activity, setActivity]   = useState([]);
    const [actLoading, setActLoading] = useState(false);
    useEffect(() => {
      if (tab !== "activity" || !lead?.id) return;
      let alive = true;
      setActLoading(true);
      apiGetLeadActivity(lead.id)
        .then(rows => { if (alive) setActivity(Array.isArray(rows) ? rows : []); })
        .catch(()  => { if (alive) setActivity([]); })
        .finally(() => { if (alive) setActLoading(false); });
      return () => { alive = false; };
    }, [tab, lead?.id, lead?.updated_at]);

    // Lazy-loaded option list for the Reassign Lead picker.
    // Fetched only when Actions tab is opened, then cached for the session.
    const [profileList,  setProfileList]  = useState([]);
    const [actionPicker, setActionPicker] = useState(null); // null | "reassign"
    const [pickerQuery,  setPickerQuery]  = useState("");
    const [pickerBusy,   setPickerBusy]   = useState(false);

    useEffect(() => {
      if (tab !== "actions") return;
      let alive = true;
      if (profileList.length === 0) {
        getEnterpriseProfiles().then(p => { if (alive) setProfileList(p || []); }).catch(()=>{});
      }
      return () => { alive = false; };
    }, [tab]);

    const openPicker = (which) => { setActionPicker(actionPicker === which ? null : which); setPickerQuery(""); };
    const closePicker = () => { setActionPicker(null); setPickerQuery(""); };

    const handlePickAssignee = async (profile) => {
      if (!profile?.id) return;
      setPickerBusy(true);
      try {
        await apiUpdateLead(lead.id, { profile_id: profile.id });
        // Optimistic local update so the assignee chip updates without a full reload.
        const username = profile.username || profile.name || `Profile #${profile.id}`;
        setLeads(ls => ls.map(l => l.id === lead.id
          ? { ...l, profile_id: profile.id, assignee: username }
          : l));
        showToast(`Reassigned to ${username}`);
        closePicker();
      } catch (err) {
        showToast(err?.message || "Failed to reassign lead");
      } finally {
        setPickerBusy(false);
      }
    };

    const STAGES   = ["new","contacted","qualified","closed"];
    const stageIdx = STAGES.indexOf(lead.status);

    const TABS = [
      {id:"contact",  ico:"📞", lbl:"Contact"},
      {id:"notes",    ico:"📝", lbl: lead.notes?.length ? `Notes (${lead.notes.length})` : "Notes"},
      {id:"activity", ico:"⚡", lbl:"Activity"},
      {id:"actions",  ico:"🎯", lbl:"Actions"},
    ];

    const card = {
      borderRadius:14,
      background:"rgba(255,255,255,.72)",
      border:"1px solid rgba(0,0,0,.07)",
      overflow:"hidden",
    };
    const sectionHead = {
      padding:"10px 16px",
      background:"rgba(0,0,0,.025)",
      borderBottom:"1px solid rgba(0,0,0,.055)",
      fontSize:10.5,fontWeight:700,
      color:"#94a3b8",letterSpacing:".08em",textTransform:"uppercase",
    };

    const profileSlug = lead.name?.split(" ")[0]?.toLowerCase() || "";

    const openEmail = () => {
      if(!lead.email){ showToast("No email on file"); return; }
      setEmailLead(lead);
    };

    return createPortal(
      <div onClick={()=>setPreview(null)} style={{
        position:"fixed",inset:0,zIndex:400,
        display:"flex",alignItems:"center",justifyContent:"center",
        padding:"28px 24px",
        background:"rgba(10,14,26,.44)",
        backdropFilter:"blur(12px)",WebkitBackdropFilter:"blur(12px)",
      }}>
        <div onClick={e=>e.stopPropagation()} style={{
          width:"100%", maxWidth:860,
          height:"min(680px, calc(100vh - 56px))",
          background:"rgba(250,251,253,.99)",
          borderRadius:22,
          boxShadow:"0 48px 120px rgba(0,0,0,.26), 0 0 0 1px rgba(255,255,255,.6)",
          display:"flex", flexDirection:"column",
          overflow:"hidden",
          animation:"cardReveal .24s cubic-bezier(.16,1,.3,1)",
        }}>

          {/* colour accent strip */}
          <div style={{height:3,flexShrink:0,
            background:`linear-gradient(90deg,${col} 0%,${col}44 60%,transparent 100%)`}}/>

          <div style={{display:"flex", flex:1, overflow:"hidden"}}>

            {/* ══════════ LEFT — identity ══════════ */}
            <div style={{
              width:264, flexShrink:0,
              borderRight:"1px solid rgba(0,0,0,.07)",
              background:`linear-gradient(180deg,${col}08 0%,rgba(248,250,252,.7) 45%)`,
              display:"flex", flexDirection:"column",
              overflowY:"auto",
            }}>

              <div style={{padding:"20px 20px 16px", borderBottom:"1px solid rgba(0,0,0,.06)"}}>

                {/* close */}
                <div style={{display:"flex", justifyContent:"flex-end", marginBottom:10}}>
                  <button onClick={()=>setPreview(null)} style={{
                    width:26,height:26,borderRadius:7,
                    border:"1px solid rgba(0,0,0,.09)",
                    background:"rgba(0,0,0,.04)", cursor:"pointer",
                    fontSize:12, color:"#94a3b8",
                    display:"flex",alignItems:"center",justifyContent:"center",
                    transition:"all .14s",
                  }}
                    onMouseEnter={e=>{e.currentTarget.style.background="rgba(0,0,0,.1)";e.currentTarget.style.color="#0f172a";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="rgba(0,0,0,.04)";e.currentTarget.style.color="#94a3b8";}}>
                    ✕
                  </button>
                </div>

                {/* avatar */}
                {lead.photo ? (
                  <img src={lead.photo} alt={lead.name} style={{
                    width:68, height:68, borderRadius:20,
                    margin:"0 auto 14px", display:"block", objectFit:"cover",
                    boxShadow:`0 8px 28px ${col}42`,
                    border:"3px solid rgba(255,255,255,.6)",
                  }}/>
                ) : (
                  <div style={{
                    width:68, height:68, borderRadius:20,
                    margin:"0 auto 14px",
                    background:`linear-gradient(145deg,${col},${col}77)`,
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:24, fontWeight:900, color:"#fff",
                    boxShadow:`0 8px 28px ${col}42`,
                    border:"3px solid rgba(255,255,255,.6)",
                  }}>{initials}</div>
                )}

                {/* name */}
                <div style={{
                  fontFamily:"var(--fd)", fontSize:18, fontWeight:400,
                  color:"#0f172a", letterSpacing:"-.4px", lineHeight:1.2,
                  textAlign:"center", marginBottom:4,
                }}>{lead.name}</div>

                <div style={{fontSize:12, color:"#64748b", textAlign:"center", lineHeight:1.5, marginBottom:14}}>
                  {lead.company || "—"}
                  {lead.industry && <><br/><span style={{color:"#94a3b8"}}>{lead.industry}</span></>}
                </div>

                {/* status + score badges */}
                <div style={{display:"flex", flexWrap:"wrap", gap:5, justifyContent:"center", marginBottom:14}}>
                  <span style={{
                    fontSize:10.5, fontWeight:700, padding:"3px 10px",
                    borderRadius:20, background:statusBg[lead.status],
                    color:col, border:`1px solid ${col}22`,
                  }}>{STATUS_LABEL[lead.status] || lead.status}</span>
                  <span style={{
                    fontSize:10.5, fontWeight:700, padding:"3px 10px",
                    borderRadius:20, background:`${scoreColor}0e`,
                    color:scoreColor, border:`1px solid ${scoreColor}22`,
                  }}>✦ {lead.aiScore} · {scoreLabel}</span>
                </div>

                {/* 3-stat strip */}
                <div style={{
                  display:"grid", gridTemplateColumns:"repeat(3,1fr)",
                  borderRadius:11, overflow:"hidden",
                  border:"1px solid rgba(0,0,0,.07)",
                }}>
                  {[
                    {lbl:"AI Score", val:lead.aiScore, c:scoreColor},
                    {lbl:"NFC Taps", val:nfcTaps,      c:"#0891b2"},
                    {lbl:"Days",     val:daysActive,   c:"#059669"},
                  ].map(({lbl,val,c},i)=>(
                    <div key={lbl} style={{
                      padding:"9px 0", textAlign:"center",
                      borderRight:i<2?"1px solid rgba(0,0,0,.07)":"none",
                      background:"rgba(255,255,255,.55)",
                    }}>
                      <div style={{fontSize:15,fontWeight:800,color:c,letterSpacing:"-.4px",lineHeight:1}}>{val}</div>
                      <div style={{fontSize:9.5,color:"#94a3b8",marginTop:2,fontWeight:500}}>{lbl}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* details */}
              <div style={{padding:"14px 16px"}}>
                <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",letterSpacing:".08em",textTransform:"uppercase",marginBottom:9}}>Details</div>
                <div style={{...card, marginBottom:12}}>
                  {[
                    {ico:"📡", lbl:"Source",   val:lead.source},
                    {ico:"📅", lbl:"Captured", val:lead.date||"—"},
                    {ico:"📍", lbl:"Location", val:[lead.city,lead.country].filter(Boolean).join(", ")||"—"},
                    {ico:"👤", lbl:"Assignee", val:lead.assignee||"Unassigned"},
                  ].map(({ico,lbl,val},i,arr)=>(
                    <div key={lbl} style={{
                      display:"flex", alignItems:"center", gap:9,
                      padding:"8px 12px",
                      borderBottom:i<arr.length-1?"1px solid rgba(0,0,0,.05)":"none",
                    }}>
                      <span style={{fontSize:12, flexShrink:0, width:16, textAlign:"center"}}>{ico}</span>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{fontSize:9.5,color:"#94a3b8",fontWeight:600,textTransform:"uppercase",letterSpacing:".05em"}}>{lbl}</div>
                        <div style={{fontSize:12,fontWeight:600,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textTransform:"capitalize"}}>{val}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* AI insight */}
                <div style={{
                  borderRadius:12, padding:"11px 13px",
                  background:"linear-gradient(135deg,rgba(124,58,237,.07),rgba(15,23,42,.03))",
                  border:"1px solid rgba(124,58,237,.14)",
                  marginBottom:12,
                }}>
                  <div style={{fontSize:10,fontWeight:700,color:"#7c3aed",marginBottom:5,letterSpacing:".05em",textTransform:"uppercase"}}>✦ AI Insight</div>
                  <div style={{fontSize:11.5,color:"#475569",lineHeight:1.6}}>{aiInsight}</div>
                </div>

                {/* pipeline */}
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94a3b8",letterSpacing:".08em",textTransform:"uppercase",marginBottom:8}}>Pipeline Stage</div>

                  <div style={{position:"relative",marginBottom:10}}>
                    <div style={{height:3,borderRadius:3,background:"rgba(0,0,0,.08)"}}/>
                    <div style={{
                      position:"absolute",top:0,left:0,height:3,borderRadius:3,
                      width:`${(stageIdx/(STAGES.length-1))*100}%`,
                      background:`linear-gradient(90deg,${col},${col}88)`,
                      transition:"width .4s cubic-bezier(.16,1,.3,1)",
                    }}/>
                    <div style={{
                      display:"flex", justifyContent:"space-between",
                      position:"absolute", top:"-5px", left:0, right:0,
                    }}>
                      {STAGES.map((_,i)=>(
                        <div key={i} style={{
                          width:13,height:13,borderRadius:"50%",
                          background: i<=stageIdx ? col : "rgba(0,0,0,.1)",
                          border:"2px solid rgba(255,255,255,.9)",
                          boxShadow: i<=stageIdx ? `0 0 6px ${col}66` : "none",
                          transition:"all .3s",
                        }}/>
                      ))}
                    </div>
                  </div>

                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                    {STAGES.map((s,i)=>(
                      <div key={s} style={{fontSize:9.5,textAlign:"center",color:i<=stageIdx?col:"#cbd5e1",fontWeight:i===stageIdx?700:500,textTransform:"capitalize",width:`${100/STAGES.length}%`}}>{s}</div>
                    ))}
                  </div>

                  <div style={{display:"flex",flexDirection:"column",gap:4}}>
                    {STAGES.map((s)=>{
                      const active = lead.status===s;
                      const sC = statusCol[s];
                      return (
                        <div key={s} onClick={(e)=>{ e.stopPropagation(); if(!active) changeStatus(lead.id,s); }} style={{
                          display:"flex",alignItems:"center",gap:8,
                          padding:"6px 9px", borderRadius:8,cursor:active?"default":"pointer",
                          background:active?`${sC}0e`:"transparent",
                          border:active?`1px solid ${sC}22`:"1px solid transparent",
                          transition:"all .13s",
                        }}
                          onMouseEnter={e=>{ if(!active) e.currentTarget.style.background="rgba(0,0,0,.03)"; }}
                          onMouseLeave={e=>{ if(!active) e.currentTarget.style.background="transparent"; }}>
                          <div style={{
                            width:6,height:6,borderRadius:"50%",flexShrink:0,
                            background:active?sC:"rgba(0,0,0,.12)",
                            boxShadow:active?`0 0 5px ${sC}77`:"none",
                          }}/>
                          <span style={{fontSize:11.5,fontWeight:active?700:480,color:active?sC:"#94a3b8",textTransform:"capitalize",flex:1}}>{s}</span>
                          {active && <span style={{fontSize:9,color:sC,fontWeight:800,letterSpacing:".05em"}}>CURRENT</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* footer actions */}
              <div style={{
                marginTop:"auto",
                padding:"11px 14px",
                borderTop:"1px solid rgba(0,0,0,.06)",
                background:"rgba(248,250,252,.97)",
                display:"flex", gap:6, flexShrink:0,
              }}>
                <button className="btn bs" style={{flex:1,justifyContent:"center",fontSize:11.5,gap:6}} onClick={()=>downloadCSV(lead)}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  CSV
                </button>
                <button className="btn bs" style={{flex:1,justifyContent:"center",fontSize:11.5,gap:6}} onClick={()=>{ setPreview(null); setEditLead(lead); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Edit
                </button>
                <button className="btn bd" title="Delete lead"
                  style={{width:32,justifyContent:"center",padding:0,flexShrink:0}}
                  onClick={(e)=>{ e.stopPropagation(); setDelConfirm(lead.id); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                    <line x1="10" y1="11" x2="10" y2="17"/>
                    <line x1="14" y1="11" x2="14" y2="17"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* ══════════ RIGHT — tabs ══════════ */}
            <div style={{flex:1, display:"flex", flexDirection:"column", overflow:"hidden", background:"rgba(248,250,252,.6)"}}>

              <div style={{
                display:"flex",
                borderBottom:"1px solid rgba(0,0,0,.07)",
                background:"rgba(252,253,255,.98)",
                flexShrink:0,
              }}>
                {TABS.map(t=>(
                  <button key={t.id} onClick={()=>setTab(t.id)} style={{
                    flex:1, padding:"12px 6px 10px",
                    border:"none", cursor:"pointer",
                    fontFamily:"var(--f)", fontSize:11.5,
                    fontWeight:tab===t.id ? 680 : 460,
                    color: tab===t.id ? col : "#94a3b8",
                    background:"transparent",
                    borderBottom: tab===t.id ? `2px solid ${col}` : "2px solid transparent",
                    transition:"all .15s",
                    display:"flex", flexDirection:"column", alignItems:"center", gap:3,
                  }}
                    onMouseEnter={e=>{ if(tab!==t.id) e.currentTarget.style.color="#475569"; }}
                    onMouseLeave={e=>{ if(tab!==t.id) e.currentTarget.style.color="#94a3b8"; }}>
                    <span style={{fontSize:15}}>{t.ico}</span>
                    <span>{t.lbl}</span>
                  </button>
                ))}
              </div>

              <div style={{flex:1, overflowY:"auto", padding:"18px 20px"}}>

                {/* ─── CONTACT ─── */}
                {tab==="contact" && (
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    <div style={card}>
                      <div style={sectionHead}>Contact Information</div>
                      {[
                        {ico:"✉️", lbl:"Email",    val:lead.email||"—",   href:lead.email?`mailto:${lead.email}`:null,  c:"#0891b2"},
                        {ico:"📞", lbl:"Phone",    val:lead.phone||"—",   href:lead.phone?`tel:${lead.phone}`:null,     c:"#059669"},
                        {ico:"🏢", lbl:"Company",  val:lead.company||"—", href:null,                                    c:"#64748b"},
                        {ico:"🌐", lbl:"Profile",  val:lead.website || (profileSlug?`gotaps.co/${profileSlug}`:"—"), href:lead.website || null, c:"#7c3aed"},
                      ].map(({ico,lbl,val,href,c},i,arr)=>(
                        <div key={lbl} style={{
                          display:"flex", alignItems:"center", gap:13,
                          padding:"12px 16px",
                          borderBottom:i<arr.length-1?"1px solid rgba(0,0,0,.05)":"none",
                        }}>
                          <div style={{
                            width:38,height:38,borderRadius:11,flexShrink:0,
                            background:`${c}0d`, border:`1px solid ${c}18`,
                            display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,
                          }}>{ico}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:10,color:"#94a3b8",fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",marginBottom:2}}>{lbl}</div>
                            {href
                              ? <a href={href} target="_blank" rel="noreferrer" style={{fontSize:13,color:c,fontWeight:600,textDecoration:"none",display:"block",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{val}</a>
                              : <div style={{fontSize:13,fontWeight:600,color:"#0f172a",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{val}</div>
                            }
                          </div>
                          {href && (
                            <a href={href} target="_blank" rel="noreferrer" style={{
                              width:28,height:28,borderRadius:8,flexShrink:0,
                              background:`${c}0d`,border:`1px solid ${c}18`,
                              display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:12,color:c,textDecoration:"none",
                            }}>↗</a>
                          )}
                        </div>
                      ))}
                    </div>

                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      {[
                        {ico:"📧",lbl:"Send Email",   c:"#0891b2", action:openEmail},
                        {ico:"📞",lbl:"Call Now",     c:"#059669", action:()=>{ if(lead.phone) window.location.href=`tel:${lead.phone}`; else showToast("No phone on file"); }},
                        {ico:"💬",lbl:"Send Message", c:"#7c3aed", disabled:true},
                        {ico:"📅",lbl:"Schedule Meet",c:"#d97706", disabled:true},
                      ].map(a=>(
                        <button key={a.lbl} onClick={a.disabled?undefined:a.action} disabled={!!a.disabled}
                          title={a.disabled?"Coming soon":undefined}
                          style={{
                            display:"flex",alignItems:"center",gap:9,
                            padding:"11px 14px",borderRadius:12,
                            border:`1px solid ${a.c}${a.disabled?"10":"18"}`,background:`${a.c}${a.disabled?"04":"07"}`,
                            cursor:a.disabled?"not-allowed":"pointer",fontFamily:"var(--f)",
                            fontSize:12.5,fontWeight:640,color:a.disabled?"#94a3b8":"#0f172a",
                            opacity:a.disabled?.6:1,
                            transition:"all .14s",
                          }}
                          onMouseEnter={e=>{ if(a.disabled)return; e.currentTarget.style.background=`${a.c}13`;e.currentTarget.style.transform="translateY(-1px)";}}
                          onMouseLeave={e=>{ if(a.disabled)return; e.currentTarget.style.background=`${a.c}07`;e.currentTarget.style.transform="";}}>
                          <span style={{fontSize:16,filter:a.disabled?"grayscale(.6)":"none"}}>{a.ico}</span>{a.lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* ─── NOTES ─── */}
                {tab==="notes" && (
                  <div style={{display:"flex",flexDirection:"column",gap:11}}>
                    <div style={{...card, padding:"14px"}}>
                      <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".07em",textTransform:"uppercase",marginBottom:8}}>New Note</div>
                      <textarea
                        placeholder="Write a note about this lead…"
                        value={addNoteFor===lead.id ? noteInput : localNote}
                        onChange={e=>{setLocalNote(e.target.value);setAddNoteFor(lead.id);setNoteInput(e.target.value);}}
                        style={{
                          width:"100%",minHeight:72,resize:"vertical",
                          border:"1px solid rgba(8,145,178,.18)",borderRadius:9,
                          padding:"10px 12px",fontFamily:"var(--f)",fontSize:13,
                          color:"#0f172a",background:"rgba(255,255,255,.85)",
                          outline:"none",boxSizing:"border-box",lineHeight:1.6,
                        }}
                      />
                      <button className="btn bp" style={{marginTop:8,width:"100%",justifyContent:"center",fontSize:12.5}}
                        onClick={()=>{addNote(lead.id,noteInput||localNote);setLocalNote("");}}>
                        + Add Note
                      </button>
                    </div>

                    {(lead.notes||[]).length===0 ? (
                      <div style={{textAlign:"center",padding:"32px 20px",borderRadius:14,border:"1px dashed rgba(0,0,0,.1)"}}>
                        <div style={{fontSize:28,marginBottom:8}}>📝</div>
                        <div style={{fontSize:13,fontWeight:600,color:"#64748b",marginBottom:3}}>No notes yet</div>
                        <div style={{fontSize:12,color:"#94a3b8"}}>Add your first note above</div>
                      </div>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",gap:7}}>
                        {(lead.notes||[]).map((n,i)=>{
                          const isEditing = editingNote && editingNote.leadId===lead.id && editingNote.idx===i;
                          return (
                            <NoteRow
                              key={i}
                              note={n}
                              accentColor={col}
                              editing={isEditing}
                              onStartEdit={()=>setEditingNote({leadId:lead.id, idx:i})}
                              onCancelEdit={()=>setEditingNote(null)}
                              onSave={(text)=>{ setEditingNote(null); saveNoteAt(lead.id, i, text); }}
                              onDelete={()=>saveNoteAt(lead.id, i, "")}
                            />
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ─── ACTIVITY ─── */}
                {tab==="activity" && (
                  <div>
                    <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".08em",textTransform:"uppercase",marginBottom:14}}>Recent Activity</div>
                    {actLoading ? (
                      <div style={{textAlign:"center",padding:"24px 0",fontSize:12,color:"#94a3b8"}}>Loading activity…</div>
                    ) : activity.length === 0 ? (
                      <div style={{textAlign:"center",padding:"32px 20px",borderRadius:14,border:"1px dashed rgba(0,0,0,.1)"}}>
                        <div style={{fontSize:28,marginBottom:8}}>⚡</div>
                        <div style={{fontSize:13,fontWeight:600,color:"#64748b",marginBottom:3}}>No activity yet</div>
                        <div style={{fontSize:12,color:"#94a3b8"}}>Events will appear here as you work this lead</div>
                      </div>
                    ) : (
                      <div style={{display:"flex",flexDirection:"column",gap:0}}>
                        {activity.map((a,i)=>{
                          const v = ACTIVITY_VISUAL[a.type] || {ico:"•", c:"#64748b"};
                          return (
                            <div key={a.id ?? i} style={{display:"flex",gap:13,position:"relative",paddingBottom:i<activity.length-1?18:0}}>
                              {i<activity.length-1 && (
                                <div style={{position:"absolute",left:14,top:30,bottom:0,width:1.5,background:"rgba(0,0,0,.07)"}}/>
                              )}
                              <div style={{
                                width:30,height:30,borderRadius:9,flexShrink:0,zIndex:1,
                                background:`${v.c}10`,border:`1px solid ${v.c}1e`,
                                display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,
                              }}>{v.ico}</div>
                              <div style={{paddingTop:4}}>
                                <div style={{fontSize:12.5,fontWeight:600,color:"#0f172a",marginBottom:2}}>{a.description}</div>
                                <div style={{fontSize:11,color:"#94a3b8"}}>{fmtTime(a.created_at)}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ─── ACTIONS ─── */}
                {tab==="actions" && (
                  <div style={{display:"flex",flexDirection:"column",gap:12}}>
                    <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".08em",textTransform:"uppercase",marginBottom:2}}>Quick Actions</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                      {[
                        {ico:"📧",lbl:"Send Email",     c:"#0891b2",sub:"via your inbox", action:openEmail},
                        {ico:"📞",lbl:"Call Now",        c:"#059669",sub:"open dialer",   action:()=>{ if(lead.phone) window.location.href=`tel:${lead.phone}`; else showToast("No phone on file"); }},
                        {ico:"💬",lbl:"Send Message",    c:"#7c3aed",sub:"Coming soon",   disabled:true},
                        {ico:"📅",lbl:"Schedule Meet",   c:"#d97706",sub:"Coming soon",   disabled:true},
                        {ico:"👤",lbl:"Reassign Lead",   c:"#64748b",sub: actionPicker==="reassign" ? "tap to close" : "pick member",    action:()=>openPicker("reassign")},
                      ].map(a=>(
                        <button key={a.lbl} onClick={a.disabled?undefined:a.action} disabled={!!a.disabled}
                          title={a.disabled?"Coming soon":undefined}
                          style={{
                            display:"flex",flexDirection:"column",alignItems:"flex-start",gap:3,
                            padding:"13px 14px",borderRadius:12,
                            border:`1px solid ${a.c}${a.disabled?"10":"18"}`,background:`${a.c}${a.disabled?"03":"06"}`,
                            cursor:a.disabled?"not-allowed":"pointer",fontFamily:"var(--f)",transition:"all .14s",textAlign:"left",
                            opacity:a.disabled?.6:1,
                          }}
                          onMouseEnter={e=>{ if(a.disabled)return; e.currentTarget.style.background=`${a.c}12`;e.currentTarget.style.transform="translateY(-1px)";}}
                          onMouseLeave={e=>{ if(a.disabled)return; e.currentTarget.style.background=`${a.c}06`;e.currentTarget.style.transform="";}}>
                          <span style={{fontSize:19,filter:a.disabled?"grayscale(.6)":"none"}}>{a.ico}</span>
                          <span style={{fontSize:12.5,fontWeight:660,color:a.disabled?"#94a3b8":"#0f172a"}}>{a.lbl}</span>
                          <span style={{fontSize:10.5,color:"#94a3b8"}}>{a.sub}</span>
                        </button>
                      ))}
                    </div>

                    {actionPicker === "reassign" && (() => {
                      const opts = profileList.map(p => ({
                        id: p.id,
                        label: p.username || p.name || `Profile #${p.id}`,
                        sub: p.email || "",
                        raw: p,
                      }));
                      const filtered = !pickerQuery
                        ? opts
                        : opts.filter(o => (o.label || "").toLowerCase().includes(pickerQuery.toLowerCase()));
                      const accent = "#64748b";
                      return (
                        <div style={{
                          borderRadius: 12,
                          background: "rgba(255,255,255,.97)",
                          border: "1px solid rgba(0,0,0,.08)",
                          boxShadow: "0 12px 32px rgba(0,0,0,.08)",
                          overflow: "hidden",
                        }}>
                          <div style={{
                            padding: "10px 14px", display:"flex", alignItems:"center", justifyContent:"space-between",
                            borderBottom: "1px solid rgba(0,0,0,.06)",
                            background: "rgba(248,250,252,.7)",
                          }}>
                            <div style={{fontSize:10.5, fontWeight:700, color:accent, letterSpacing:".08em", textTransform:"uppercase"}}>
                              Reassign To
                            </div>
                            <button onClick={closePicker} style={{
                              border:"none", background:"transparent", cursor:"pointer",
                              fontSize:12, color:"#94a3b8", padding:4,
                            }}>✕</button>
                          </div>
                          <div style={{padding:"8px 10px", borderBottom:"1px solid rgba(0,0,0,.05)"}}>
                            <input className="inp" autoFocus
                              placeholder="Search team members…"
                              value={pickerQuery}
                              onChange={e=>setPickerQuery(e.target.value)}
                              style={{fontSize:12.5, padding:"6px 10px"}}/>
                          </div>
                          <div style={{maxHeight: 220, overflowY: "auto"}}>
                            {pickerBusy ? (
                              <div style={{padding:"16px 14px", fontSize:12, color:"#94a3b8", textAlign:"center"}}>Working…</div>
                            ) : filtered.length === 0 ? (
                              <div style={{padding:"16px 14px", fontSize:12, color:"#94a3b8", textAlign:"center"}}>
                                {opts.length === 0 ? "No team members available" : "No matches"}
                              </div>
                            ) : filtered.map(o => (
                              <div key={o.id}
                                onClick={()=>handlePickAssignee(o.raw)}
                                style={{
                                  padding:"9px 14px", cursor:"pointer",
                                  borderBottom:"1px solid rgba(0,0,0,.04)",
                                  display:"flex", alignItems:"center", justifyContent:"space-between", gap:8,
                                }}
                                onMouseEnter={e=>e.currentTarget.style.background = "rgba(0,0,0,.03)"}
                                onMouseLeave={e=>e.currentTarget.style.background = "transparent"}>
                                <div style={{minWidth:0, flex:1}}>
                                  <div style={{fontSize:12.5, fontWeight:600, color:"#0f172a", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{o.label}</div>
                                  {o.sub && <div style={{fontSize:11, color:"#94a3b8", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{o.sub}</div>}
                                </div>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.4" style={{flexShrink:0, opacity:.5}}>
                                  <polyline points="9 18 15 12 9 6"/>
                                </svg>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    <div style={{
                      padding:"14px 16px",borderRadius:12,
                      background:"rgba(220,38,38,.03)",
                      border:"1px solid rgba(220,38,38,.1)",
                    }}>
                      <div style={{fontSize:10.5,fontWeight:700,color:"#dc2626",letterSpacing:".07em",textTransform:"uppercase",marginBottom:10}}>Danger Zone</div>
                      <div style={{display:"flex",gap:8}}>
                        <button className="btn bs" style={{flex:1,justifyContent:"center",fontSize:12,gap:6}} onClick={()=>downloadCSV(lead)}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Export CSV
                        </button>
                        <button onClick={(e)=>{ e.stopPropagation(); setDelConfirm(lead.id); }} style={{
                          flex:1,padding:"9px",borderRadius:10,cursor:"pointer",
                          border:"1px solid rgba(220,38,38,.22)",
                          background:"rgba(220,38,38,.06)",
                          fontFamily:"var(--f)",fontSize:12,fontWeight:640,color:"#dc2626",
                          display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                        }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            <line x1="10" y1="11" x2="10" y2="17"/>
                            <line x1="14" y1="11" x2="14" y2="17"/>
                          </svg>
                          Delete Lead
                        </button>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }, []);

  /* ══════════════ DELETE CONFIRM ══════════════ */
  // Portaled to document.body so it always sits above the LeadDetailPanel,
  // regardless of any stacking context an ancestor of PageLeads might create.
  const DeleteConfirm = useMemo(() => function DeleteConfirm({lead}) {
    const { setDelConfirm, handleDeleteLead } = handlersRef.current;
    return createPortal(
    <div onClick={(e)=>{ if(e.target===e.currentTarget) setDelConfirm(null); }} style={{position:"fixed",inset:0,zIndex:600,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(15,23,42,.55)",backdropFilter:"blur(8px)",WebkitBackdropFilter:"blur(8px)"}}>
      <div style={{width:380,background:"rgba(255,255,255,.98)",borderRadius:20,padding:"28px 28px 24px",boxShadow:"0 24px 60px rgba(0,0,0,.22)",border:"1px solid rgba(255,255,255,.8)",animation:"modalPop .22s cubic-bezier(.16,1,.3,1)"}}>
        <div style={{width:52,height:52,borderRadius:14,background:"rgba(220,38,38,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:24,margin:"0 auto 16px"}}>🗑</div>
        <div style={{textAlign:"center",marginBottom:18}}>
          <div style={{fontSize:16,fontWeight:720,color:"#0f172a",marginBottom:6}}>Delete Lead?</div>
          <div style={{fontSize:13,color:"#64748b",lineHeight:1.5}}>
            <b style={{color:"#0f172a"}}>{lead.name}</b> from {lead.company} will be permanently removed. This action cannot be undone.
          </div>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button className="btn bs" style={{flex:1,justifyContent:"center"}} onClick={()=>setDelConfirm(null)}>Cancel</button>
          <button className="btn" onClick={()=>handleDeleteLead(lead.id)}
            style={{flex:1,justifyContent:"center",background:"linear-gradient(135deg,#dc2626,#b91c1c)",color:"#fff",border:"none",borderRadius:11,padding:"10px",cursor:"pointer",fontSize:13,fontWeight:640,fontFamily:"var(--f)",display:"flex",alignItems:"center",gap:6}}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              <line x1="10" y1="11" x2="10" y2="17"/>
              <line x1="14" y1="11" x2="14" y2="17"/>
            </svg>
            Delete
          </button>
        </div>
      </div>
    </div>,
      document.body
    );
  }, []);

  /* ══════════════ ADD / EDIT LEAD MODAL ══════════════ */
  const LeadFormModal = ({ editing }) => {
    const isEdit = !!editing;
    const [mode,setMode] = useState("manual");
    const [aiInput,setAiInput] = useState("");
    const [aiLoading,setAiLoading] = useState(false);
    const [aiResult,setAiResult] = useState(null);
    const [photo,setPhoto] = useState(null);
    const [photoPreview,setPhotoPreview] = useState(isEdit && editing.photo ? fullPhotoUrl(editing.photo) : null);
    const [profiles,setProfiles] = useState([]);
    const [profileId,setProfileId] = useState(isEdit ? (editing.profile_id ? String(editing.profile_id) : "") : "");
    const [errors,setErrors] = useState({});
    const [form,setForm] = useState(isEdit ? {
      name:editing.name||"",email:editing.email||"",phone:editing.phone||"",company:editing.company||"",
      country:editing.country||"",state:editing.state||"",city:editing.city||"",industry:editing.industry||"",
      note:editing.notes?.[0]||"",source:editing.source||"Manual",status:editing.status||"new",
      job_title:editing.job_title||"",website:editing.website||"",linkedin_url:editing.linkedin_url||"",address:editing.address||"",
    } : {
      name:"",email:"",phone:"",company:"",country:"",state:"",city:"",industry:"",note:"",source:"Manual",status:"new",job_title:"",website:"",linkedin_url:"",address:"",
    });
    const sf = (k,v) => { setForm(f=>({...f,[k]:v})); setErrors(e=>({...e,[k]:null})); };
    const handlePhoto = (e) => {
      const file = e.target.files[0];
      if(file){setPhoto(file);setPhotoPreview(URL.createObjectURL(file));}
    };
    const isManualType = !isEdit || editing.type === 3;

    // Load profiles for dropdown
    useEffect(() => {
      getEnterpriseProfiles().then(p => setProfiles(p)).catch(() => {});
    }, []);

    const closeModal = () => { if(isEdit) setEditLead(null); else setShowAdd(false); };

    // URL validation helper
    const isValidUrl = (str) => {
      if (!str) return true;
      try { new URL(str); return true; } catch { return false; }
    };

    const validate = () => {
      const errs = {};
      if (!form.name.trim()) errs.name = "Full Name is required";
      if (!form.email.trim()) errs.email = "Email is required";
      if (!form.phone.trim()) errs.phone = "Phone is required";
      if (!isEdit && !profileId) errs.profile_id = "Please select a user/profile";
      if (form.website && !isValidUrl(form.website)) errs.website = "Enter a valid URL (e.g. https://example.com)";
      if (form.linkedin_url && !isValidUrl(form.linkedin_url)) errs.linkedin_url = "Enter a valid URL (e.g. https://linkedin.com/in/name)";
      setErrors(errs);
      return Object.keys(errs).length === 0;
    };

    const AI_ENRICHED = {
      "tony@example.com":   {name:"Anthony Morrison",company:"Nexus Global",country:"United States",state:"California",city:"San Francisco",industry:"SaaS",phone:"+1 415 555 0192",aiScore:94,position:"VP of Sales"},
      "sarah@apex.io":      {name:"Sarah Chen",company:"Apex Ventures",country:"United States",state:"New York",city:"New York",industry:"Finance",phone:"+1 212 555 0133",aiScore:87,position:"Partner"},
      "default":            {name:"Alex Johnson",company:"TechCorp Solutions",country:"United States",state:"Texas",city:"Austin",industry:"Tech",phone:"+1 512 555 0177",aiScore:78,position:"Director of Marketing"},
    };

    const runAI = () => {
      if(!aiInput.trim()) return;
      setAiLoading(true);
      setTimeout(()=>{
        const key = Object.keys(AI_ENRICHED).find(k=>aiInput.toLowerCase().includes(k)) || "default";
        const enriched = AI_ENRICHED[key];
        setAiResult(enriched);
        setForm(f=>({...f,...enriched}));
        setAiLoading(false);
      }, 1800);
    };

    const [saving, setSaving] = useState(false);
    const handleSave = async () => {
      if (!validate()) return;
      setSaving(true);
      try {
        const payload = {
          name: form.name, email: form.email, phone: form.phone,
          company: form.company || null, job_title: form.job_title || null,
          website: form.website || null, linkedin_url: form.linkedin_url || null,
          country: form.country || null, state: form.state || null,
          city: form.city || null, address: form.address || null,
          industry: form.industry || null, note: form.note || null,
          status: form.status || "new",
        };
        if (isEdit) {
          // Only send profile_id if user picked a different assignee — avoids
          // unnecessarily writing the same viewer_id back to the DB.
          if (profileId && String(profileId) !== String(editing.profile_id || "")) {
            payload.profile_id = profileId;
          }
          const res = await apiUpdateLead(editing.id, payload);
          if (res.lead) {
            updateLead(editing.id, { ...payload, notes: form.note ? [form.note] : [], aiScore: res.lead.score || editing.aiScore, photo: fullPhotoUrl(res.lead.photo) || editing.photo });
          } else {
            updateLead(editing.id, { ...payload, notes: form.note ? [form.note] : [] });
          }
          closeModal();
          showToast(`${form.name||"Lead"} updated successfully!`);
        } else {
          payload.profile_id = profileId || null;
          const res = await apiAddLead(payload);
          if (res.lead) { setLeads(ls=>[mapLead(res.lead),...ls]); } else { await loadLeads(); }
          closeModal();
          showToast(`${form.name||"Lead"} added successfully!`);
        }
      } catch (err) {
        if (err.errors) {
          setErrors(err.errors);
        }
        showToast(err.message || (isEdit ? "Failed to update lead" : "Failed to add lead"));
      } finally {
        setSaving(false);
      }
    };

    const isValid = form.name.trim()&&form.email.trim()&&form.phone.trim()&&(isEdit||profileId);

    const SectionLabel = ({children}) => (
      <div style={{fontSize:10.5,fontWeight:700,color:"#94a3b8",letterSpacing:".08em",textTransform:"uppercase",marginBottom:10,marginTop:18}}>{children}</div>
    );

    return createPortal(
      <div className="modal-overlay">
        <div style={{width:"100%",maxWidth:560,maxHeight:"85vh",background:"rgba(255,255,255,.97)",backdropFilter:"blur(40px)",borderRadius:22,boxShadow:"0 32px 80px rgba(0,0,0,.18),0 8px 32px rgba(0,0,0,.1)",display:"flex",flexDirection:"column",overflow:"hidden"}}>

          {/* Header */}
          <div style={{padding:"22px 24px 0",flexShrink:0}}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:18}}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div style={{width:44,height:44,borderRadius:12,background:isEdit?"rgba(217,119,6,.1)":"rgba(8,145,178,.1)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,boxShadow:isEdit?"0 4px 14px rgba(217,119,6,.15)":"0 4px 14px rgba(8,145,178,.15)"}}>{isEdit?"✏️":"➕"}</div>
                <div>
                  <div style={{fontSize:17,fontWeight:720,color:"#0f172a",letterSpacing:"-.3px"}}>{isEdit?"Edit Lead":"Add New Lead"}</div>
                  <div style={{fontSize:12,color:"#94a3b8",marginTop:2}}>{isEdit?"Update lead information":"Manual entry or AI enrichment"}</div>
                </div>
              </div>
              <button onClick={closeModal}
                style={{width:32,height:32,borderRadius:10,border:"1px solid rgba(0,0,0,.1)",background:"rgba(0,0,0,.04)",cursor:"pointer",fontSize:14,color:"#64748b",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            </div>

            {/* Mode toggle — only for add mode */}
            {!isEdit&&(
              <div style={{display:"flex",gap:6,background:"rgba(0,0,0,.04)",padding:4,borderRadius:11,marginBottom:4}}>
                {[["manual","📝 Manual Entry"],["ai","✦ AI Enrichment"]].map(([m,l])=>(
                  <button key={m} onClick={()=>setMode(m)}
                    style={{flex:1,padding:"8px",border:"none",borderRadius:8,cursor:"pointer",fontSize:12.5,fontWeight:620,fontFamily:"var(--f)",transition:"all .18s",
                      background:mode===m?"rgba(255,255,255,.96)":"transparent",
                      color:mode===m?"#0f172a":"#64748b",
                      boxShadow:mode===m?"0 2px 10px rgba(0,0,0,.08)":"none"}}>{l}</button>
                ))}
              </div>
            )}
          </div>

          {/* Body — scrollable */}
          <div style={{flex:1,overflowY:"auto",padding:"0 24px 24px"}}>

            {/* ── AI ENRICHMENT — only for add mode ── */}
            {!isEdit&&mode==="ai"&&(
              <div>
                <SectionLabel>AI Enrichment</SectionLabel>
                <div style={{marginBottom:14,padding:"14px 16px",background:"linear-gradient(135deg,rgba(124,58,237,.06),rgba(8,145,178,.04))",border:"1px solid rgba(124,58,237,.16)",borderRadius:12}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <span style={{fontSize:18}}>✦</span>
                    <div style={{fontSize:13,fontWeight:700,color:"#0f172a"}}>AI Lead Enrichment</div>
                  </div>
                  <div style={{fontSize:12,color:"#64748b",lineHeight:1.5}}>Enter an email, company domain, or person's name. AI will automatically enrich the lead profile with contact info, company details, and social profiles.</div>
                </div>

                <div className="fg">
                  <label>Email / Domain / Name</label>
                  <div style={{display:"flex",gap:9}}>
                    <input className="inp" placeholder="e.g. tony@example.com or companyname.io"
                      value={aiInput} onChange={e=>setAiInput(e.target.value)}
                      onKeyDown={e=>e.key==="Enter"&&runAI()} style={{flex:1}}/>
                    <button className="btn bai" style={{flexShrink:0,gap:6,whiteSpace:"nowrap"}} onClick={runAI} disabled={!aiInput.trim()||aiLoading}>
                      {aiLoading?<><div className="ai-pulse"/>Enriching…</>:<>✦ Enrich</>}
                    </button>
                  </div>
                </div>

                {aiLoading&&(
                  <div style={{margin:"16px 0",padding:"16px",background:"rgba(124,58,237,.05)",borderRadius:12,border:"1px solid rgba(124,58,237,.14)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                      <div className="ai-pulse" style={{width:10,height:10}}/>
                      <div style={{fontSize:12.5,fontWeight:600,color:"#7c3aed"}}>AI is enriching your lead…</div>
                    </div>
                    {["Searching company database…","Resolving contact details…","Scoring lead quality…"].map((msg,i)=>(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:8,marginBottom:5}}>
                        <div style={{width:5,height:5,borderRadius:"50%",background:"rgba(124,58,237,.4)"}}/>
                        <div style={{fontSize:11.5,color:"#94a3b8"}}>{msg}</div>
                      </div>
                    ))}
                  </div>
                )}

                {aiResult&&!aiLoading&&(
                  <div style={{margin:"16px 0",padding:"14px 16px",background:"rgba(5,150,105,.05)",borderRadius:12,border:"1px solid rgba(5,150,105,.2)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
                      <span style={{fontSize:16}}>✅</span>
                      <div style={{fontSize:12.5,fontWeight:700,color:"#059669"}}>Lead enriched successfully!</div>
                      <span style={{marginLeft:"auto",fontSize:10.5,fontWeight:660,padding:"2px 8px",borderRadius:20,background:"rgba(124,58,237,.1)",color:"#7c3aed"}}>AI Score {aiResult.aiScore}</span>
                    </div>
                    {[["Name",aiResult.name],["Company",aiResult.company],["Position",aiResult.position],["Location",`${aiResult.city}, ${aiResult.state}`],["Industry",aiResult.industry]].map(([k,v])=>(
                      <div key={k} style={{display:"flex",gap:8,fontSize:11.5,marginBottom:4}}>
                        <span style={{color:"#94a3b8",minWidth:70}}>{k}:</span>
                        <span style={{color:"#0f172a",fontWeight:580}}>{v}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Assigned To — searchable dropdown of enterprise users ── */}
            <SectionLabel>Assigned To{!isEdit && " *"}</SectionLabel>
            <div className="fg">
              <label>{isEdit ? "Reassign Lead" : "User / Profile *"}</label>
              <SearchableSelect
                value={profileId}
                onChange={(v)=>{ setProfileId(String(v)); setErrors(er=>({...er,profile_id:null})); }}
                options={profiles.map(p => ({
                  value: p.id,
                  label: p.username || p.name || `Profile #${p.id}`,
                }))}
                placeholder={isEdit ? "Keep current assignee" : "Select a profile…"}
                error={!!errors.profile_id}
              />
              {errors.profile_id && <div style={{fontSize:11,color:"#ef4444",marginTop:4}}>{errors.profile_id}</div>}
            </div>

            {/* ── Photo — only for manual type leads ── */}
            {isManualType&&(<>
              <SectionLabel>Photo</SectionLabel>
              <div className="fg" style={{display:"flex",alignItems:"center",gap:14}}>
                {photoPreview&&<img src={photoPreview} alt="Preview" style={{width:52,height:52,borderRadius:"50%",objectFit:"cover",border:"2px solid rgba(0,0,0,.08)"}}/>}
                <label style={{display:"inline-flex",alignItems:"center",gap:8,padding:"8px 16px",borderRadius:10,border:"1px dashed rgba(0,0,0,.18)",background:"rgba(0,0,0,.02)",cursor:"pointer",fontSize:12.5,color:"#64748b",fontWeight:580}}>
                  📷 {photoPreview?"Change Photo":"Upload Photo"}
                  <input type="file" accept="image/*" onChange={handlePhoto} style={{display:"none"}}/>
                </label>
                {photoPreview&&<button onClick={()=>{setPhoto(null);setPhotoPreview(null);}} style={{border:"none",background:"none",cursor:"pointer",fontSize:11,color:"#ef4444",fontWeight:600}}>Remove</button>}
              </div>
            </>)}

            {/* ── FORM FIELDS ── */}
            <SectionLabel>Contact Information</SectionLabel>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div className="fg">
                <label>Full Name *</label>
                <input className="inp" placeholder="John Smith" value={form.name} onChange={e=>sf("name",e.target.value)} style={{borderColor:errors.name?"#ef4444":""}}/>
                {errors.name&&<div style={{fontSize:11,color:"#ef4444",marginTop:4}}>{errors.name}</div>}
              </div>
              <div className="fg">
                <label>Email *</label>
                <input className="inp" type="email" placeholder="john@company.com" value={form.email} onChange={e=>sf("email",e.target.value)} style={{borderColor:errors.email?"#ef4444":""}}/>
                {errors.email&&<div style={{fontSize:11,color:"#ef4444",marginTop:4}}>{errors.email}</div>}
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div className="fg">
                <label>Phone *</label>
                <input className="inp" placeholder="+1 555 000 0000" value={form.phone} onChange={e=>sf("phone",e.target.value)} style={{borderColor:errors.phone?"#ef4444":""}}/>
                {errors.phone&&<div style={{fontSize:11,color:"#ef4444",marginTop:4}}>{errors.phone}</div>}
              </div>
              <div className="fg"><label>Company</label><input className="inp" placeholder="Company name" value={form.company} onChange={e=>sf("company",e.target.value)}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div className="fg"><label>Job Title</label><input className="inp" placeholder="e.g. VP of Sales" value={form.job_title} onChange={e=>sf("job_title",e.target.value)}/></div>
              <div className="fg">
                <label>Website</label>
                <input className="inp" type="url" placeholder="https://example.com" value={form.website} onChange={e=>sf("website",e.target.value)} style={{borderColor:errors.website?"#ef4444":""}}/>
                {errors.website&&<div style={{fontSize:11,color:"#ef4444",marginTop:4}}>{errors.website}</div>}
              </div>
            </div>
            <div className="fg">
              <label>LinkedIn URL</label>
              <input className="inp" type="url" placeholder="https://linkedin.com/in/username" value={form.linkedin_url} onChange={e=>sf("linkedin_url",e.target.value)} style={{borderColor:errors.linkedin_url?"#ef4444":""}}/>
              {errors.linkedin_url&&<div style={{fontSize:11,color:"#ef4444",marginTop:4}}>{errors.linkedin_url}</div>}
            </div>

            <SectionLabel>Location</SectionLabel>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div className="fg"><label>Country</label><input className="inp" placeholder="United States" value={form.country} onChange={e=>sf("country",e.target.value)}/></div>
              <div className="fg"><label>State</label><input className="inp" placeholder="New York" value={form.state} onChange={e=>sf("state",e.target.value)}/></div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
              <div className="fg"><label>City</label><input className="inp" placeholder="New York" value={form.city} onChange={e=>sf("city",e.target.value)}/></div>
              <div className="fg"><label>Address</label><input className="inp" placeholder="123 Main St" value={form.address} onChange={e=>sf("address",e.target.value)}/></div>
            </div>
            <div className="fg">
              <label>Industry</label>
              <select className="inp" value={form.industry} onChange={e=>sf("industry",e.target.value)} style={{cursor:"pointer"}}>
                {["","Tech","Finance","Real Estate","Media","Biotech","Defense","Science","Sales","Marketing","Healthcare","Education","Other"].map(v=><option key={v} value={v}>{v||"Select…"}</option>)}
              </select>
            </div>

            <SectionLabel>Pipeline Status</SectionLabel>
            <div className="fg">
              <div style={{display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:6}}>
                {[
                  {v:"new",       l:"New",       c:"#0891b2"},
                  {v:"contacted", l:"Contacted", c:"#d97706"},
                  {v:"qualified", l:"Qualified", c:"#059669"},
                  {v:"closed",    l:"Closed",    c:"#64748b"},
                ].map(s => {
                  const active = form.status === s.v;
                  return (
                    <button key={s.v} type="button" onClick={()=>sf("status", s.v)} style={{
                      padding:"9px 6px", borderRadius:10,
                      fontSize:11.5, fontWeight: active ? 700 : 560,
                      fontFamily:"var(--f)", cursor:"pointer",
                      border: active ? `1px solid ${s.c}55` : "1px solid rgba(0,0,0,.1)",
                      background: active ? `${s.c}10` : "rgba(255,255,255,.55)",
                      color: active ? s.c : "#64748b",
                      display:"flex", flexDirection:"column", alignItems:"center", gap:5,
                      transition:"all .14s",
                    }}>
                      <span style={{
                        width:8, height:8, borderRadius:"50%",
                        background: active ? s.c : "rgba(0,0,0,.15)",
                        boxShadow: active ? `0 0 6px ${s.c}66` : "none",
                      }}/>
                      {s.l}
                    </button>
                  );
                })}
              </div>
            </div>

            <SectionLabel>Notes</SectionLabel>
            <div className="fg">
              <textarea className="inp" placeholder="Add a comment about this lead…" value={form.note} onChange={e=>sf("note",e.target.value)} style={{minHeight:66,resize:"vertical"}}/>
            </div>
          </div>

          {/* Footer */}
          <div style={{padding:"16px 24px",borderTop:"1px solid rgba(0,0,0,.07)",display:"flex",alignItems:"center",justifyContent:"flex-end",gap:9,flexShrink:0}}>
            <button className="btn bs" style={{padding:"9px 20px",fontSize:13}} onClick={closeModal}>Cancel</button>
            <button className="btn bp" style={{padding:"9px 22px",fontSize:13,opacity:(isValid&&!saving)?1:.55,cursor:(isValid&&!saving)?"pointer":"not-allowed"}} onClick={handleSave} disabled={!isValid||saving}>
              {saving ? "Saving…" : isEdit ? "Update Lead" : "Save Lead"}
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  /* ══════════════ KPI STRIP ══════════════ */
  const totalLeads = leads.length;
  const newCount   = leads.filter(l=>l.status==="new").length;
  const qualCount  = leads.filter(l=>l.status==="qualified").length;
  const avgScore   = Math.round(leads.reduce((s,l)=>s+l.aiScore,0)/Math.max(leads.length,1));

  return (
    <div className="pg fi">
      {preview && selectedLead && <LeadDetailPanel lead={selectedLead} initialTab={previewIntent.tab} tabNonce={previewIntent.nonce}/>}
      {delConfirm && <DeleteConfirm lead={leads.find(l=>l.id===delConfirm)}/>}
      {showAdd   && <LeadFormModal/>}
      {editLead  && <LeadFormModal editing={editLead}/>}
      {emailLead && (
        <LeadEmailModal
          lead={emailLead}
          sender={{
            companyName: user?.company_name,
            name:        user?.name,
            email:       user?.email,
          }}
          onClose={()=>setEmailLead(null)}
          onSend={async (subject, body) => {
            try {
              await apiSendLeadEmail([emailLead.email], subject, body);
              showToast(`Email sent to ${emailLead.name}`);
              setEmailLead(null);
            } catch (err) {
              showToast(err?.message || "Failed to send email");
            }
          }}
        />
      )}

      <PH eyebrow="CRM" title="All Leads" sub={`${totalLeads} contacts · AI-scored`}
        action={
          <div style={{display:"flex",gap:9}}>
            <button className="btn bs" style={{gap:6,fontSize:12}} onClick={()=>showToast("Exporting all leads…")}>⬇ Export All</button>
            <button className="btn bp" style={{gap:6}} onClick={()=>setShowAdd(true)}>+ Add Lead</button>
          </div>
        }
      />

      {/* KPI strip */}
      <div className="g4" style={{marginBottom:20}}>
        {[
          {lbl:"Total Leads",   val:totalLeads, ico:"🎯", col:"#0891b2", bg:"rgba(8,145,178,.1)"},
          {lbl:"New",           val:newCount,   ico:"✨", col:"#059669", bg:"rgba(5,150,105,.1)"},
          {lbl:"Qualified",     val:qualCount,  ico:"⭐", col:"#d97706", bg:"rgba(217,119,6,.1)"},
          {lbl:"Avg AI Score",  val:avgScore,   ico:"✦",  col:"#7c3aed", bg:"rgba(124,58,237,.1)"},
        ].map(({lbl,val,ico,col,bg},i)=>(
          <div key={lbl} className={`card fi s${i+1}`} style={{padding:20,position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",top:0,left:0,right:0,height:2.5,background:col,opacity:.6}}/>
            <div style={{width:36,height:36,borderRadius:11,background:bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,marginBottom:12}}>{ico}</div>
            <div style={{fontSize:10.5,color:"#94a3b8",fontWeight:600,letterSpacing:".04em",marginBottom:3}}>{lbl}</div>
            <div style={{fontSize:24,fontWeight:760,color:"#0f172a",letterSpacing:"-1px"}}>{val}</div>
          </div>
        ))}
      </div>

      {/* Filter + search */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:4,background:"rgba(255,255,255,.7)",padding:4,borderRadius:11,border:"1px solid rgba(255,255,255,.9)"}}>
          {["all","new","contacted","qualified","closed"].map(s=>(
            <button key={s} onClick={()=>setFilter(s)}
              style={{padding:"5px 13px",borderRadius:8,border:"none",cursor:"pointer",fontSize:11.5,fontWeight:filter===s?660:500,fontFamily:"var(--f)",background:filter===s?"rgba(255,255,255,.96)":"transparent",color:filter===s?"#0f172a":"#94a3b8",boxShadow:filter===s?"0 2px 8px rgba(0,0,0,.08)":"none",transition:"all .15s",textTransform:"capitalize"}}>
              {s==="all"?`All (${totalLeads})`:s}
            </button>
          ))}
        </div>
        <input className="inp" placeholder="🔍  Search leads…" value={search} onChange={e=>setSearch(e.target.value)}
          style={{flex:1,maxWidth:260,fontSize:12}}/>
        <div style={{marginLeft:"auto",fontSize:12,color:"#94a3b8"}}>{rows.length} result{rows.length!==1?"s":""}</div>
      </div>

      {/* Table */}
      <div className="card-s">
        <table className="tbl">
          <thead>
            <tr>
              <th>Lead</th>
              <th>Location</th>
              <th>AI Score</th>
              <th>Source</th>
              <th>Assignee</th>
              <th>Status</th>
              <th>Date</th>
              <th style={{textAlign:"right",paddingRight:18}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(l=>{
              const sc = statusCol[l.status]||"#64748b";
              return (
                <tr key={l.id} style={{cursor:"pointer"}} onClick={()=>openPreviewWith(l.id,"contact")}>
                  <td>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:34,height:34,borderRadius:10,background:`linear-gradient(135deg,${sc},${sc}88)`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",flexShrink:0,boxShadow:`0 2px 8px ${sc}33`}}>
                        {l.name.split(" ").map(n=>n[0]).join("").slice(0,2)}
                      </div>
                      <div>
                        <div style={{fontWeight:640,fontSize:12.5,color:"#0f172a"}}>{l.name}</div>
                        <div style={{fontSize:10.5,color:"#94a3b8",marginTop:1}}>{l.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{fontSize:12,color:"#475569"}}>{l.city||"—"}</div>
                    <div style={{fontSize:10.5,color:"#94a3b8",marginTop:1}}>{l.country||""}</div>
                  </td>
                  <td><AiScoreRing score={l.aiScore}/></td>
                  <td><span className="badge b-blue" style={{fontSize:10.5}}>{l.source}</span></td>
                  <td style={{fontSize:12,color:"#475569"}}>{l.assignee||<span className="badge b-amber" style={{cursor:"pointer",fontSize:10}} onClick={e=>{e.stopPropagation();showToast("Auto-assigning…");}}>✦ Assign</span>}</td>
                  <td onClick={e=>e.stopPropagation()} style={{position:"relative"}}>
                    <span onClick={e=>{e.stopPropagation();setStatusDrop(statusDrop===l.id?null:l.id);}}
                      style={{fontSize:10.5,fontWeight:660,padding:"3px 9px",borderRadius:20,background:statusBg[l.status],color:sc,textTransform:"capitalize",border:`1px solid ${sc}22`,cursor:"pointer",display:"inline-flex",alignItems:"center",gap:4}}>
                      {l.status}<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                    </span>
                    {statusDrop===l.id&&(
                      <div style={{position:"absolute",top:"100%",left:0,marginTop:4,zIndex:50,background:"rgba(255,255,255,.98)",backdropFilter:"blur(20px)",borderRadius:12,boxShadow:"0 12px 40px rgba(0,0,0,.16)",border:"1px solid rgba(0,0,0,.08)",padding:4,minWidth:130}}>
                        {STATUSES.filter(s=>s!==l.status).map(s=>(
                          <div key={s} onClick={()=>changeStatus(l.id,s)}
                            style={{padding:"7px 12px",fontSize:11.5,fontWeight:580,color:statusCol[s],cursor:"pointer",borderRadius:8,textTransform:"capitalize",display:"flex",alignItems:"center",gap:8,transition:"background .12s"}}
                            onMouseEnter={e=>e.currentTarget.style.background=statusBg[s]} onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            <span style={{width:7,height:7,borderRadius:"50%",background:statusCol[s],flexShrink:0}}/>{s}
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{fontSize:11.5,color:"#94a3b8"}}>{l.date}</td>
                  <td onClick={e=>e.stopPropagation()}>
                    <div style={{display:"flex",gap:6,justifyContent:"flex-end",padding:"0 8px"}}>
                      {/* Preview */}
                      <button title="Preview lead" onClick={()=>openPreviewWith(l.id,"contact")}
                        style={{width:32,height:32,borderRadius:9,border:"none",background:"rgba(8,145,178,.08)",cursor:"pointer",color:"#0891b2",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background="rgba(8,145,178,.18)";e.currentTarget.style.transform="scale(1.1)";}}
                        onMouseLeave={e=>{e.currentTarget.style.background="rgba(8,145,178,.08)";e.currentTarget.style.transform="scale(1)";}}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      </button>
                      {/* Comment */}
                      <button title={l.notes.length>0?"View comment":"Add comment"} onClick={()=>openPreviewWith(l.id,"notes")}
                        style={{width:32,height:32,borderRadius:9,border:"none",background:"rgba(217,119,6,.08)",cursor:"pointer",color:"#d97706",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s",position:"relative"}}
                        onMouseEnter={e=>{e.currentTarget.style.background="rgba(217,119,6,.18)";e.currentTarget.style.transform="scale(1.1)";}}
                        onMouseLeave={e=>{e.currentTarget.style.background="rgba(217,119,6,.08)";e.currentTarget.style.transform="scale(1)";}}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        {l.notes.length>0&&<div style={{position:"absolute",top:-3,right:-3,width:15,height:15,borderRadius:"50%",background:"linear-gradient(135deg,#d97706,#f59e0b)",fontSize:8,color:"#fff",fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 6px rgba(217,119,6,.4)"}}>{l.notes.length}</div>}
                      </button>
                      {/* Download */}
                      <button title="Download CSV" onClick={()=>downloadCSV(l)}
                        style={{width:32,height:32,borderRadius:9,border:"none",background:"rgba(5,150,105,.08)",cursor:"pointer",color:"#059669",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background="rgba(5,150,105,.18)";e.currentTarget.style.transform="scale(1.1)";}}
                        onMouseLeave={e=>{e.currentTarget.style.background="rgba(5,150,105,.08)";e.currentTarget.style.transform="scale(1)";}}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      </button>
                      {/* Delete */}
                      <button title="Delete lead" onClick={()=>setDelConfirm(l.id)}
                        style={{width:32,height:32,borderRadius:9,border:"none",background:"rgba(220,38,38,.08)",cursor:"pointer",color:"#dc2626",display:"flex",alignItems:"center",justifyContent:"center",transition:"all .2s"}}
                        onMouseEnter={e=>{e.currentTarget.style.background="rgba(220,38,38,.18)";e.currentTarget.style.transform="scale(1.1)";}}
                        onMouseLeave={e=>{e.currentTarget.style.background="rgba(220,38,38,.08)";e.currentTarget.style.transform="scale(1)";}}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {loading&&(
          <div style={{textAlign:"center",padding:"48px 0",color:"#94a3b8"}}>
            <div style={{fontSize:14,fontWeight:580,color:"#64748b"}}>Loading leads…</div>
          </div>
        )}
        {!loading&&rows.length===0&&(
          <div style={{textAlign:"center",padding:"48px 0",color:"#94a3b8"}}>
            <div style={{fontSize:36,marginBottom:10}}>🎯</div>
            <div style={{fontSize:14,fontWeight:580,color:"#64748b",marginBottom:6}}>No leads found</div>
            <div style={{fontSize:12,marginBottom:18}}>{search?"Try a different search term":"Add your first lead to get started"}</div>
            <button className="btn bp" onClick={()=>setShowAdd(true)}>+ Add Lead</button>
          </div>
        )}
      </div>
    </div>
  );
});


